/**
 * How far the land is, and which way it lies, at every point on the water.
 *
 * The waves that run at a coast need to know where the coast is, and they need
 * to know it at every pixel rather than at every vertex. That turned out to be
 * the whole difficulty. The sea is drawn as one flat sheet a couple of thousand
 * tiles across with a vertex every thirty six tiles, and it carried a single
 * number saying "no bank anywhere near" at all of them: a swell built on that
 * has nothing to follow, so it either does nothing at all or, worse, does the
 * same thing everywhere at once and the whole ocean brightens and dims like a
 * lamp on a dimmer.
 *
 * So the answer is a picture rather than an attribute. A small map is drawn over
 * the ground the reader can see, one texel to a couple of tiles, holding how far
 * that texel is from the nearest land and which way that land lies. The shader
 * reads it by world position, which works the same on a sheet with four corners
 * as on a pond built out of a thousand of them.
 *
 * It is built from the ground the client has actually been sent, so it follows
 * the coastline exactly, including the coastline of an island somebody dropped
 * in a minute ago.
 */

import * as THREE from 'three';
import { SEA_LEVEL } from '@inverse/common';

/**
 * How much ground the map covers, in tiles each way.
 *
 * Smaller than it was, so the texels can be finer without the picture growing.
 * The reader is put back in the middle of it well before they reach an edge.
 */
export const SHORE_TILES = 256;

/**
 * How many texels that is: two to a tile.
 *
 * It was one to every two tiles, which is enough for a swell measured in tens
 * of tiles and nowhere near enough for foam measured in one or two: the band at
 * the bank was three texels wide, and it read as a row of blocks. Then one to a
 * tile, which is where the last of it stuck: whether a texel was land was a
 * question about a whole tile, so the coastline the foam followed was the tile
 * grid, and the surf came out as a staircase of squares.
 */
export const SHORE_TEXELS = 512;

/**
 * The furthest a reading can express, in tiles.
 *
 * Anything beyond this is simply open water, and the swell has faded out long
 * before. Held to a distance a byte can carry at a useful resolution: a quarter
 * of a tile a step, which is finer than anything reads at.
 */
export const SHORE_REACH = 64;

/**
 * How far either side of the waterline the close reading can express, in tiles.
 *
 * A byte over sixty four tiles is a quarter of a tile a step, which the swell
 * cannot tell from smooth and the foam can: a ribbon a third of a tile thick
 * drawn on a field that moves in quarter tile stairs is a ribbon with stairs in
 * it. So the alpha channel carries the first few tiles over again, finely, and
 * everything that happens at the bank reads that instead.
 */
export const SHORE_CLOSE = 16;

/** How far the reader may stray before it is drawn again around them. */
const STRAY = 40;

/**
 * How the ground's own height is written into a picture.
 *
 * The lowest it can say, and how much it spans, over sixteen bits. Four
 * thousandths of a tile a step, which is far finer than the tenth of a tile the
 * waterline is drawn to.
 */
export const BED_LOW = -64;
export const BED_SPAN = 256;

/**
 * How much ground the bed picture covers, and how finely.
 *
 * Its own stretch, smaller and finer than the map beside it. The waterline is
 * worked out from this and from nothing else, so how sharp the water's edge is
 * is exactly how many texels there are to a tile: at the two a tile the map has,
 * the line can only be placed to within half a tile, and a coast drawn to half a
 * tile wanders in and out of the sand in a way that reads as carelessness.
 *
 * Four and a half texels to a tile puts it within a quarter of one. The stretch
 * is wide enough that it still reaches past the ground, which is sent for sixty
 * four tiles round whoever is reading: a bed that ran out first would leave a
 * ring of coast with no water's edge on it.
 */
export const BED_TILES = 224;
export const BED_TEXELS = 1024;

export interface ShoreMap {
  texture: THREE.DataTexture;
  /**
   * The ground under the water, as a picture.
   *
   * The one thing that puts a shoreline where the shoreline is. The surface of
   * the water is built out of whole tiles, so nothing about the mesh knows
   * where the water's edge falls inside one; the ground does, because it is a
   * surface through the corner heights and it comes up through the water along
   * a line that owes the tile grid nothing. Read here per pixel, the depth of
   * the water is known everywhere, and the waterline is where it reaches
   * nought.
   *
   * Held apart from the map beside it because it is read differently: that one
   * is blurred between texels on purpose, and a height split across two bytes
   * cannot be, so this is read sharp and mixed by hand.
   */
  bed: THREE.DataTexture;
  /** The stretch of world the map covers: x and z of a corner, then width and height. */
  rect: THREE.Vector4;
  /** And the stretch the bed covers, which is a smaller one drawn finer. */
  bedRect: THREE.Vector4;
}

interface Ground {
  width: number;
  height: number;
  originX: number;
  originZ: number;
  flags: ArrayLike<number>;
  squares: ReadonlySet<string>;
  /** Every corner of the ground, which is what puts the waterline inside a tile. */
  heights: ArrayLike<number>;
  /** The surface over each wet tile. */
  water: ArrayLike<number>;
  /** Whether the sea sheet is drawn over this region at all, which it is not in a cave. */
  sea?: boolean;
}

const WATER_FLAG = 1 << 6;

/** How big a square of streamed ground is, which is how the client keys them. */
const SQUARE = 64;

/**
 * Whether one tile is water, as far as the shore is concerned.
 *
 * Ground that has not arrived is neither: it is left as water so a coastline
 * does not appear along the edge of what has been streamed in, which would put
 * a line of surf across the open sea wherever the loaded ground happens to end.
 */
function wet(ground: Ground, atX: number, atZ: number): boolean {
  const tx = Math.floor(atX);
  const tz = Math.floor(atZ);
  if (tx < 0 || tz < 0 || tx >= ground.width || tz >= ground.height) return true;

  // Ground that has not arrived carries no flags, and no flags reads as land: a
  // coastline would appear along the edge of whatever has been streamed in, and
  // the open sea would grow a ring of surf round the reader that followed them
  // about. What has not arrived is water until it says otherwise.
  const square = `${Math.floor(tx / SQUARE) * SQUARE}.${Math.floor(tz / SQUARE) * SQUARE}`;
  if (!ground.squares.has(square)) return true;

  const at = tz * ground.width + tx;
  if (((ground.flags[at] ?? 0) & WATER_FLAG) === 0) {
    /*
     * A dry tile under the sea is sea, as far as anybody looking can tell.
     *
     * The sheet is drawn over every region with a sea, wherever the ground is
     * lower than it, whatever the tile underneath says. Reading only the flag,
     * a tile of land pushed down under the sea in the tools stayed land here:
     * the water closed over it and the surf went on breaking on a beach that
     * was no longer there, in a ring round a patch of empty sea.
     */
    if (!ground.sea) return false;
    return bedAt(ground, atX, atZ) < SEA_LEVEL;
  }

  /*
   * Inside a wet tile, where the water actually ends.
   *
   * This is the whole of the staircase. Whether a point was land used to be a
   * question about the tile it sat in, so the coastline the surf followed was
   * the tile grid itself, and the foam came out as a row of squares stepping
   * along it. The ground is not stepped: its corners are, and between them it
   * is a smooth surface. So the height is read at the point rather than at the
   * tile, and the shore is where that surface comes up through the water. On a
   * shelving beach that line wanders across a tile at whatever angle the ground
   * happens to lie at, which is what a shoreline is.
   */
  return bedAt(ground, atX, atZ) < (ground.water[at] ?? 0);
}

/**
 * The ground itself at a point, read through the corners round it.
 *
 * Ground that has not arrived reads as far below the sea, for the same reason
 * it reads as water above: the height array is nought where nothing has been
 * streamed in, and nought is the sea's own level, so every stretch of open
 * water outside the squares round the reader came out a hair deep. That is not
 * a small mistake. Water a hair deep is drawn as the water's edge: it went
 * see-through, took surf, and stopped dead in a straight line at the edge of
 * the map the shader reads, which showed as a seam lying across the ocean with
 * a different sea on the other side of it.
 *
 * The value here is the one the shader uses where it has no map at all, so the
 * two agree and there is no line between them.
 */
function bedAt(ground: Ground, atX: number, atZ: number): number {
  const tx = Math.floor(atX);
  const tz = Math.floor(atZ);
  if (tx < 0 || tz < 0 || tx >= ground.width || tz >= ground.height) return BED_LOW;

  const square = `${Math.floor(tx / SQUARE) * SQUARE}.${Math.floor(tz / SQUARE) * SQUARE}`;
  if (!ground.squares.has(square)) return BED_LOW;

  const stride = ground.width + 1;

  const held = (cx: number, cz: number): number =>
    ground.heights[
      Math.min(Math.max(cz, 0), ground.height) * stride + Math.min(Math.max(cx, 0), ground.width)
    ] ?? 0;

  const fx = atX - tx;
  const fz = atZ - tz;

  const a = held(tx, tz);
  const b = held(tx + 1, tz);
  const c = held(tx, tz + 1);
  const d = held(tx + 1, tz + 1);

  return (a + (b - a) * fx) * (1 - fz) + (c + (d - c) * fx) * fz;
}

/**
 * Draws the map around a point.
 *
 * A vector distance transform: every texel carries the offset to the nearest
 * land rather than only how far it is, which is what gives a direction as well
 * as a distance and gives both of them exactly. Two sweeps, the usual way, each
 * carrying what the last one knew.
 */
export function drawShoreMap(ground: Ground, around: { tx: number; tz: number }): ShoreMap {
  const step = SHORE_TILES / SHORE_TEXELS;
  const fromX = Math.floor(around.tx - SHORE_TILES / 2);
  const fromZ = Math.floor(around.tz - SHORE_TILES / 2);

  const side = SHORE_TEXELS;
  /**
   * How far every texel is from the nearest one the test picks out, and which
   * way it lies.
   *
   * A vector distance transform: every texel carries the offset to the nearest
   * seed rather than only how far it is, which is what gives a direction as
   * well as a distance and gives both of them exactly. Two sweeps, the usual
   * way, each carrying what the last one knew.
   */
  const distancesFrom = (
    seed: (at: number) => boolean,
  ): { offsetX: Float32Array; offsetZ: Float32Array } => {
    const offsetX = new Float32Array(side * side);
    const offsetZ = new Float32Array(side * side);
    const known = new Uint8Array(side * side);

    for (let at = 0; at < side * side; at++) {
      if (seed(at)) {
        known[at] = 1;
      } else {
        offsetX[at] = 1e6;
        offsetZ[at] = 1e6;
      }
    }

    const shorterThrough = (at: number, from: number, byX: number, byZ: number): void => {
      if (known[from] !== 1) return;

      const wasX = offsetX[at] ?? 1e6;
      const wasZ = offsetZ[at] ?? 1e6;
      const nowX = (offsetX[from] ?? 0) + byX;
      const nowZ = (offsetZ[from] ?? 0) + byZ;

      if (nowX * nowX + nowZ * nowZ >= wasX * wasX + wasZ * wasZ) return;

      offsetX[at] = nowX;
      offsetZ[at] = nowZ;
      known[at] = 1;
    };

    for (let z = 0; z < side; z++) {
      for (let x = 0; x < side; x++) {
        const at = z * side + x;
        if (x > 0) shorterThrough(at, at - 1, -1, 0);
        if (z > 0) shorterThrough(at, at - side, 0, -1);
        if (x > 0 && z > 0) shorterThrough(at, at - side - 1, -1, -1);
        if (x + 1 < side && z > 0) shorterThrough(at, at - side + 1, 1, -1);
      }
    }

    for (let z = side - 1; z >= 0; z--) {
      for (let x = side - 1; x >= 0; x--) {
        const at = z * side + x;
        if (x + 1 < side) shorterThrough(at, at + 1, 1, 0);
        if (z + 1 < side) shorterThrough(at, at + side, 0, 1);
        if (x + 1 < side && z + 1 < side) shorterThrough(at, at + side + 1, 1, 1);
        if (x > 0 && z + 1 < side) shorterThrough(at, at + side - 1, -1, 1);
      }
    }

    return { offsetX, offsetZ };
  };

  /** Which texels are dry, read at the point rather than at the tile. */
  const land = new Uint8Array(side * side);
  for (let z = 0; z < side; z++) {
    for (let x = 0; x < side; x++) {
      const tx = fromX + (x + 0.5) * step - ground.originX;
      const tz = fromZ + (z + 0.5) * step - ground.originZ;
      land[z * side + x] = wet(ground, tx, tz) ? 0 : 1;
    }
  }

  const toLand = distancesFrom((at) => land[at] === 1);

  /*
   * And the same again from the water, which is what makes the reading signed.
   *
   * A distance to land alone is nought everywhere on land, so there is no way
   * to say how far inside it a point is. That matters because the surface of
   * the water is built out of whole tiles and reaches past the waterline onto
   * the land: to hide that overhang without also rubbing out a tile of real
   * water, the shader has to be able to tell the overhang from the sea, and one
   * is on the near side of nought and the other on the far side.
   */
  const toWater = distancesFrom((at) => land[at] === 0);

  const offsetX = toLand.offsetX;
  const offsetZ = toLand.offsetZ;

  const data = new Uint8Array(side * side * 4);

  for (let at = 0; at < side * side; at++) {
    const byX = (offsetX[at] ?? 1e6) * step;
    const byZ = (offsetZ[at] ?? 1e6) * step;
    const away = Math.min(SHORE_REACH, Math.hypot(byX, byZ));

    data[at * 4] = Math.round((away / SHORE_REACH) * 255);

    // Which way the land lies from here. Nothing where there is no land within
    // reach, so the shader can tell open water from a beach it cannot see.
    const reach = Math.hypot(byX, byZ);
    const towardX = reach > 0.001 && reach < SHORE_REACH * 4 ? byX / reach : 0;
    const towardZ = reach > 0.001 && reach < SHORE_REACH * 4 ? byZ / reach : 0;

    data[at * 4 + 1] = Math.round((towardX * 0.5 + 0.5) * 255);
    data[at * 4 + 2] = Math.round((towardZ * 0.5 + 0.5) * 255);
    /*
     * The same distance close in, signed, at sixteen steps to the tile.
     *
     * Positive out at sea and negative on land, with nought exactly on the
     * waterline. The sign is what lets the shader tell the last stretch of the
     * water's surface, which is drawn over land because the surface is made of
     * whole tiles, from water that is actually there. Fading both left a tile
     * of clear water between the surf and the sand.
     */
    const inland = Math.hypot((toWater.offsetX[at] ?? 0) * step, (toWater.offsetZ[at] ?? 0) * step);
    const signed = land[at] === 1 ? -inland : away;

    data[at * 4 + 3] = Math.round(Math.min(255, Math.max(0, 128 + signed * (128 / SHORE_CLOSE))));
  }

  /*
   * And the ground itself, written down at the same texels.
   *
   * Sixteen bits of it, split across two bytes, which is why this picture is
   * read sharp: a number split across two bytes cannot be blurred between
   * texels without the low byte wrapping and throwing the height a long way
   * out. The shader reads the four texels round a point and mixes them itself.
   */
  const bedSide = BED_TEXELS;
  const bedStep = BED_TILES / BED_TEXELS;
  const bedFromX = Math.floor(around.tx - BED_TILES / 2);
  const bedFromZ = Math.floor(around.tz - BED_TILES / 2);
  const bedData = new Uint8Array(bedSide * bedSide * 4);

  for (let z = 0; z < bedSide; z++) {
    for (let x = 0; x < bedSide; x++) {
      const at = z * bedSide + x;
      const tx = bedFromX + (x + 0.5) * bedStep - ground.originX;
      const tz = bedFromZ + (z + 0.5) * bedStep - ground.originZ;

      const held = Math.min(1, Math.max(0, (bedAt(ground, tx, tz) - BED_LOW) / BED_SPAN));
      const whole = Math.round(held * 65535);

      bedData[at * 4] = whole >> 8;
      bedData[at * 4 + 1] = whole & 255;
      bedData[at * 4 + 2] = 0;
      bedData[at * 4 + 3] = 255;
    }
  }

  const bed = new THREE.DataTexture(bedData, bedSide, bedSide, THREE.RGBAFormat);
  bed.magFilter = THREE.NearestFilter;
  bed.minFilter = THREE.NearestFilter;
  bed.wrapS = THREE.ClampToEdgeWrapping;
  bed.wrapT = THREE.ClampToEdgeWrapping;
  bed.needsUpdate = true;

  const texture = new THREE.DataTexture(data, side, side, THREE.RGBAFormat);
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.needsUpdate = true;

  return {
    texture,
    bed,
    rect: new THREE.Vector4(fromX, fromZ, SHORE_TILES, SHORE_TILES),
    bedRect: new THREE.Vector4(bedFromX, bedFromZ, BED_TILES, BED_TILES),
  };
}

/** Whether a map drawn round one point still covers another. */
export function stillCovers(rect: THREE.Vector4, at: { tx: number; tz: number }): boolean {
  const middleX = rect.x + rect.z / 2;
  const middleZ = rect.y + rect.w / 2;
  return Math.abs(at.tx - middleX) < STRAY && Math.abs(at.tz - middleZ) < STRAY;
}
